import { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { apiScopes } from './msalConfig';
import { isAuthEnabled } from './authEnabled';

/**
 * Hook that returns the current user's roles from the Entra ID access token.
 *
 * When auth is disabled (local dev), returns an empty array and loaded=true.
 * Callers should treat empty roles as "show everything" (no restrictions).
 */
export function useUserRoles(): { roles: string[]; loaded: boolean } {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [roles, setRoles] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(!isAuthEnabled);

  useEffect(() => {
    if (!isAuthEnabled) {
      setLoaded(true);
      return;
    }

    if (!isAuthenticated || inProgress !== InteractionStatus.None) return;

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      setLoaded(true);
      return;
    }

    instance.acquireTokenSilent({ ...apiScopes, account })
      .then((response) => {
        try {
          const payload = response.accessToken.split('.')[1];
          const decoded = JSON.parse(atob(payload));
          setRoles(decoded.roles ?? []);
        } catch {
          setRoles([]);
        }
        setLoaded(true);
      })
      .catch(() => {
        setLoaded(true);
      });
  }, [isAuthenticated, inProgress, instance, accounts]);

  return { roles, loaded };
}
